<template>
  <div class="panel">
    <div class="panel-title">Désactivation d'une société Hit-Online</div>

    <div class="panel-grid">
      <hit-select
        class="min-w-[32rem] w-full"
        :label="'Société'"
        :options="companyOptions"
        :value="selectedSchemaId"
        item-id-property="id"
        :label-fields="['label']"
        clearable
        @change="onCompanyChange"
      />

      <div class="date-field">
        <label class="date-label">Date de désactivation</label>
        <input
          v-model="deactivationDate"
          class="date-input"
          type="date"
          :max="today"
        />
      </div>
    </div>

    <div class="mt-4 risk-panel">
      <div class="risk-title">Ce que cette opération va faire</div>
      <ul class="risk-list">
        <li>
          <span class="bullet">1</span>
          <span class="text">
            Passe la société en <strong>inactive</strong> dans
            <code>hitonline.company</code>.
          </span>
        </li>
        <li>
          <span class="bullet">2</span>
          <span class="text">
            Supprime les modules actifs de cette société et ajoute l’historique
            <code>UNSUBSCRIBED</code>.
          </span>
        </li>
        <li>
          <span class="bullet">3</span>
          <span class="text">
            Retire les utilisateurs du groupe de la société dans Keycloak.
          </span>
        </li>
      </ul>
      <label class="confirm-row" for="deactivate-company-confirm">
        <input
          id="deactivate-company-confirm"
          v-model="confirmed"
          type="checkbox"
          class="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        <span>Je confirme la désactivation de cette société.</span>
      </label>
    </div>

    <div class="mt-4 flex gap-2">
      <hit-button
        :label="'Désactiver la société'"
        color="accent"
        prefix-icon="warning"
        :disabled="running || !canRun"
        @click="run"
      />
      <hit-button
        :label="'Réinitialiser'"
        color="button"
        prefix-icon="reset_changes"
        :disabled="running"
        @click="resetForm"
      />
    </div>

    <div v-if="result" class="result mt-4">
      <div class="result-title" :class="{ok: result.ok, ko: !result.ok}">
        {{ result.ok ? 'Succès' : 'Erreur' }}
      </div>
      <pre class="result-pre">{{ resultText }}</pre>
    </div>
  </div>
</template>

<script>
import {defineComponent} from 'vue';
import {HitButton, HitSelect} from '@hit/components';
import {adminApi} from '@admin/services/adminApi';

export default defineComponent({
  name: 'ScriptDeactivateCompany',
  components: {HitButton, HitSelect},
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      today,
      companyOptions: [],
      selectedSchemaId: null,
      deactivationDate: today,
      confirmed: false,
      running: false,
      result: null,
    };
  },
  computed: {
    canRun() {
      return (
        !!this.selectedSchemaId && !!this.deactivationDate && this.confirmed
      );
    },
    resultText() {
      try {
        return JSON.stringify(this.result, null, 2);
      } catch {
        return String(this.result);
      }
    },
  },
  async mounted() {
    await this.loadCompanyOptions();
  },
  methods: {
    async loadCompanyOptions() {
      try {
        const res = await adminApi.listCompanyOptions({limit: 500});
        const rows = res?.rows || [];
        this.companyOptions = rows.map((r) => {
          const label = [r.designation, r.vat_no].filter(Boolean).join(' – ');
          return {id: r.schema_id || r.id, label: label || r.id};
        });
      } catch {
        this.companyOptions = [];
      }
    },
    onCompanyChange(value) {
      this.selectedSchemaId = value;
      this.result = null;
    },
    resetForm() {
      this.selectedSchemaId = null;
      this.deactivationDate = this.today;
      this.confirmed = false;
      this.result = null;
    },
    async run() {
      if (!this.canRun) return;
      this.running = true;
      this.result = null;
      try {
        const res = await adminApi.runScript(
          '/scripts/deactivate_hit_online_company/run',
          {
            schema_id: this.selectedSchemaId,
            deactivation_date: this.deactivationDate,
          }
        );
        this.result = res;
      } catch (e) {
        this.result = {ok: false, message: e?.message || String(e)};
      } finally {
        this.running = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.82);
  padding: 0.9rem;
}

.panel-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  margin-bottom: 0.75rem;
}

.panel-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.date-field {
  min-width: 20rem;
  width: 100%;
}

.date-label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  color: rgba(15, 23, 42, 0.9);
}

.date-input {
  width: 100%;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 0.6rem;
  padding: 0.52rem 0.6rem;
  background: #fff;
  color: rgba(15, 23, 42, 0.95);
}

.risk-panel {
  margin-top: 1rem;
  padding: 0.6rem 0.85rem;
  border-radius: 0.85rem;
  border: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(248, 250, 252, 0.96);
  display: grid;
  gap: 0.35rem;
  font-size: 0.88rem;
  color: rgba(15, 23, 42, 0.85);
}

.risk-title {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.9);
}

.risk-list {
  margin: 0.1rem 0 0;
  padding: 0;
  display: grid;
  gap: 0.15rem;
  list-style: none;
}

.risk-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}

.risk-list .bullet {
  flex: 0 0 auto;
  width: 1.3rem;
  height: 1.3rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: rgba(15, 23, 42, 0.92);
  background: rgba(148, 163, 184, 0.18);
}

.risk-list .text {
  flex: 1 1 auto;
}

.confirm-row {
  margin-top: 0.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.85rem;
  color: rgba(55, 65, 81, 1);
}

.result-title {
  font-weight: 700;
}
.result-title.ok {
  color: #03695a;
}
.result-title.ko {
  color: #b91c1c;
}
.result-pre {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  line-height: 1.15rem;
  background: rgba(248, 250, 252, 1);
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 0.65rem;
  padding: 0.6rem 0.65rem;
  max-height: 18rem;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
